import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { fetchCompanyList, type CompanyListItem } from "./companyAPI";

export interface CompanyDisplayData {
  id: number;
  name: string;
  profilePicture: number | null;
  website: string | null;
  industry: string;
  hqCity: string;
}

interface CompaniesState {
  companies: CompanyDisplayData[];
  status: "idle" | "loading" | "succeeded" | "failed";
  error: string | null;
  pagination: {
    count: number;
    totalPages: number;
    currentPage: number;
  } | null;
}

const initialState: CompaniesState = {
  companies: [],
  status: "idle",
  error: null,
  pagination: null,
};

const transformCompanyData = (company: CompanyListItem): CompanyDisplayData => {
  return {
    id: company.id,
    name: company.name || "N/A",
    profilePicture: company.profile_picture,
    website: company.website || "N/A",
    industry: company.industry?.name || "N/A",
    hqCity: company.city?.name || "N/A",
  };
};

export const fetchCompanies = createAsyncThunk<
  { companies: CompanyDisplayData[]; pagination: { count: number; totalPages: number; currentPage: number } | null },
  { accessToken: string },
  { rejectValue: { message: string } }
>("companies/fetchCompanies", async ({ accessToken }, { rejectWithValue }) => {
  try {
    const response = await fetchCompanyList(accessToken);
    if (response.error) {
      return rejectWithValue({ message: response.error });
    }
    const companiesData = response.data || [];
    const transformedCompanies = companiesData.map(transformCompanyData);
    
    const pagination = response.pagination ? {
      count: response.pagination.count,
      totalPages: response.pagination.total_pages,
      currentPage: response.pagination.current_page,
    } : null;
    
    return { companies: transformedCompanies, pagination };
  } catch (error) {
    if (error instanceof Error) {
      return rejectWithValue({ message: error.message });
    }
    return rejectWithValue({ message: "Unable to fetch companies. Please try again." });
  }
});

const companiesSlice = createSlice({
  name: "companies",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchCompanies.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchCompanies.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.companies = action.payload.companies;
        state.pagination = action.payload.pagination;
        state.error = null;
      })
      .addCase(fetchCompanies.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload?.message || "Failed to fetch companies.";
      });
  },
});

export default companiesSlice.reducer;

